import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Package, Plus, Search, Edit, AlertTriangle, ArrowDown, ArrowUp,
  RefreshCw, Filter, History, MoreHorizontal, Trash2, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

export default function InventoryPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null } = context;
  
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showAdjustDialog, setShowAdjustDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [adjustingItem, setAdjustingItem] = useState(null);
  
  // Form state
  const [itemForm, setItemForm] = useState({
    name: '',
    sku: '',
    category: '',
    quantity: '',
    unit: 'units',
    low_stock_threshold: '',
    cost_per_unit: ''
  });
  
  const [adjustForm, setAdjustForm] = useState({
    quantity: '',
    type: 'add',
    reason: ''
  });

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchInventory = useLatestCallback(async () => {
    setLoadError(false);
    try {
      const response = await axios.get(`${API}/inventory/items`, getHeaders());
      setInventory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch inventory:', error);
      toast.error('Could not load inventory');
      setInventory([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (token) {
      fetchInventory();
    }
  }, [token, fetchInventory]);

  const handleSaveItem = async () => {
    try {
      if (editingItem) {
        await axios.put(`${API}/inventory/items/${editingItem.id}`, itemForm, getHeaders());
        toast.success('Item updated');
      } else {
        await axios.post(`${API}/inventory/items`, { ...itemForm, restaurant_id: manager?.restaurant_id }, getHeaders());
        toast.success('Item added');
      }
      setShowItemDialog(false);
      resetItemForm();
      fetchInventory();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleAdjustStock = async () => {
    try {
      const adjustment = adjustForm.type === 'add' ? parseInt(adjustForm.quantity) : -parseInt(adjustForm.quantity);
      await axios.post(
        `${API}/inventory/items/${adjustingItem.id}/adjust`,
        { quantity: adjustment, reason: adjustForm.reason },
        getHeaders()
      );
      toast.success('Stock adjusted');
      setShowAdjustDialog(false);
      resetAdjustForm();
      fetchInventory();
    } catch (error) {
      toast.error('Failed to adjust stock');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/inventory/items/${item.id}`, getHeaders());
      toast.success('Item deleted');
      fetchInventory();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      sku: '',
      category: '',
      quantity: '',
      unit: 'units',
      low_stock_threshold: '',
      cost_per_unit: ''
    });
    setEditingItem(null);
  };

  const resetAdjustForm = () => {
    setAdjustForm({ quantity: '', type: 'add', reason: '' });
    setAdjustingItem(null);
  };

  const openEditItem = (item) => {
    setItemForm({
      name: item.name,
      sku: item.sku || '',
      category: item.category || '',
      quantity: item.quantity?.toString() || '',
      unit: item.unit || 'units',
      low_stock_threshold: item.low_stock_threshold?.toString() || '',
      cost_per_unit: item.cost_per_unit?.toString() || ''
    });
    setEditingItem(item);
    setShowItemDialog(true);
  };

  const openAdjustStock = (item) => {
    setAdjustingItem(item);
    setShowAdjustDialog(true);
  };

  // Get stock status
  const getStockStatus = (item) => {
    const qty = item.quantity || 0;
    const threshold = item.low_stock_threshold || 10;
    if (qty <= 0) return { label: 'Out of Stock', color: 'bg-red-100 text-red-800' };
    if (qty <= threshold) return { label: 'Low Stock', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'In Stock', color: 'bg-green-100 text-green-800' };
  };

  // Filter inventory
  const filteredInventory = inventory.filter(item => {
    const status = getStockStatus(item);
    if (statusFilter === 'low' && status.label !== 'Low Stock') return false;
    if (statusFilter === 'out' && status.label !== 'Out of Stock') return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        item.name?.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query) ||
        item.category?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: inventory.length,
    lowStock: inventory.filter(i => (i.quantity || 0) > 0 && (i.quantity || 0) <= (i.low_stock_threshold || 10)).length,
    outOfStock: inventory.filter(i => (i.quantity || 0) <= 0).length,
    totalValue: inventory.reduce((sum, i) => sum + ((i.quantity || 0) * (i.cost_per_unit || 0)), 0)
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="inventory-page">
      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="inventory-load-error">
          <span>Could not load inventory.</span>
          <Button variant="outline" size="sm" onClick={fetchInventory}>Try again</Button>
        </div>
      )}
      {/* Header - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="text-gray-500 text-sm md:text-base">Track and manage your stock levels</p>
        </div>
        <Button 
          className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto"
          onClick={() => { resetItemForm(); setShowItemDialog(true); }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      {/* Stats Cards - 2x2 grid on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Total Items</p>
                <p className="text-xl md:text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Package className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Low Stock</p>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">{stats.lowStock}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 md:h-6 md:w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Out of Stock</p>
                <p className="text-xl md:text-2xl font-bold text-red-600">{stats.outOfStock}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-red-100 flex items-center justify-center">
                <Package className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-3 md:p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-gray-500">Total Value</p>
                <p className="text-xl md:text-2xl font-bold text-green-600">${stats.totalValue.toFixed(2)}</p>
              </div>
              <div className="h-10 w-10 md:h-12 md:w-12 rounded-xl bg-green-100 flex items-center justify-center">
                <Check className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters - Responsive */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search inventory..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Items</SelectItem>
                  <SelectItem value="low">Low Stock</SelectItem>
                  <SelectItem value="out">Out of Stock</SelectItem>
                </SelectContent>
              </Select>
              
              <Button variant="outline" size="icon" onClick={fetchInventory} className="flex-shrink-0">
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table - Responsive with horizontal scroll */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold whitespace-nowrap">Item</TableHead>
                <TableHead className="font-semibold whitespace-nowrap hidden sm:table-cell">SKU</TableHead>
                <TableHead className="font-semibold whitespace-nowrap hidden md:table-cell">Category</TableHead>
                <TableHead className="font-semibold whitespace-nowrap">Stock Level</TableHead>
                <TableHead className="font-semibold whitespace-nowrap">Status</TableHead>
                <TableHead className="font-semibold text-right whitespace-nowrap hidden sm:table-cell">Value</TableHead>
                <TableHead className="font-semibold text-right whitespace-nowrap">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInventory.length > 0 ? (
                filteredInventory.map((item) => {
                  const status = getStockStatus(item);
                  const itemQty = item.quantity || 0;
                  const threshold = item.low_stock_threshold || 10;
                  const costPerUnit = item.cost_per_unit || 0;
                  const maxStock = threshold * 3;
                  const stockPercent = maxStock > 0 ? Math.min(100, (itemQty / maxStock) * 100) : 0;
                  
                  return (
                    <TableRow key={item.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="font-medium text-sm md:text-base">{item.name}</div>
                        <span className="text-xs text-gray-400 sm:hidden">{item.sku || '-'}</span>
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <span className="font-mono text-sm">{item.sku || '-'}</span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <Badge variant="outline">{item.category || 'Uncategorized'}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 w-20 md:w-32">
                          <div className="flex justify-between text-xs md:text-sm">
                            <span>{itemQty}</span>
                            <span className="text-gray-400 hidden md:inline">/ {maxStock}</span>
                          </div>
                          <Progress 
                            value={stockPercent} 
                            className={`h-1.5 md:h-2 ${status.label === 'Low Stock' ? '[&>div]:bg-yellow-500' : status.label === 'Out of Stock' ? '[&>div]:bg-red-500' : ''}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${status.color} text-xs`}>
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right hidden sm:table-cell">
                        <span className="font-semibold">
                          ${(itemQty * costPerUnit).toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 md:h-8 md:w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openAdjustStock(item)}>
                              <ArrowUp className="h-4 w-4 mr-2" />
                              Adjust Stock
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openEditItem(item)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Item
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <History className="h-4 w-4 mr-2" />
                              View History
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => handleDeleteItem(item)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    <Package className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                    <p>No inventory items found</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add Inventory Item'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Item Name *</Label>
                <Input
                  value={itemForm.name}
                  onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                  placeholder="e.g., Ground Beef"
                />
              </div>
              
              <div>
                <Label>SKU</Label>
                <Input
                  value={itemForm.sku}
                  onChange={(e) => setItemForm({ ...itemForm, sku: e.target.value })}
                  placeholder="BEEF-001"
                />
              </div>
              
              <div>
                <Label>Category</Label>
                <Input
                  value={itemForm.category}
                  onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
                  placeholder="Proteins"
                />
              </div>
              
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  value={itemForm.quantity}
                  onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })}
                  placeholder="0"
                />
              </div>
              
              <div>
                <Label>Unit</Label>
                <Select
                  value={itemForm.unit}
                  onValueChange={(val) => setItemForm({ ...itemForm, unit: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="units">Units</SelectItem>
                    <SelectItem value="lbs">Pounds (lbs)</SelectItem>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="oz">Ounces (oz)</SelectItem>
                    <SelectItem value="bags">Bags</SelectItem>
                    <SelectItem value="cases">Cases</SelectItem>
                    <SelectItem value="heads">Heads</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Low Stock Alert</Label>
                <Input
                  type="number"
                  value={itemForm.low_stock_threshold}
                  onChange={(e) => setItemForm({ ...itemForm, low_stock_threshold: e.target.value })}
                  placeholder="10"
                />
              </div>
              
              <div>
                <Label>Cost per Unit ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.cost_per_unit}
                  onChange={(e) => setItemForm({ ...itemForm, cost_per_unit: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} disabled={!itemForm.name}>
              {editingItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={showAdjustDialog} onOpenChange={setShowAdjustDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Adjust Stock: {adjustingItem?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center p-4 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-500">Current Stock</p>
              <p className="text-3xl font-bold">{adjustingItem?.quantity} {adjustingItem?.unit}</p>
            </div>
            
            <div className="flex gap-2">
              <Button
                variant={adjustForm.type === 'add' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setAdjustForm({ ...adjustForm, type: 'add' })}
              >
                <ArrowUp className="h-4 w-4 mr-2" />
                Add Stock
              </Button>
              <Button
                variant={adjustForm.type === 'remove' ? 'default' : 'outline'}
                className="flex-1"
                onClick={() => setAdjustForm({ ...adjustForm, type: 'remove' })}
              >
                <ArrowDown className="h-4 w-4 mr-2" />
                Remove Stock
              </Button>
            </div>
            
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                value={adjustForm.quantity}
                onChange={(e) => setAdjustForm({ ...adjustForm, quantity: e.target.value })}
                placeholder="Enter quantity"
              />
            </div>
            
            <div>
              <Label>Reason (optional)</Label>
              <Input
                value={adjustForm.reason}
                onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                placeholder="e.g., Received shipment, Damaged goods"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAdjustDialog(false); resetAdjustForm(); }}>Cancel</Button>
            <Button onClick={handleAdjustStock} disabled={!adjustForm.quantity}>
              {adjustForm.type === 'add' ? 'Add' : 'Remove'} Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
